import { useCallback } from 'react';
import { useDialog } from '../context/DialogContext';

export interface DuplicateNameGuardArgs {
  /** Entity noun shown in the copy, e.g. "vendor" or "catalog item". */
  entityLabel: string;
  /** The name the user is about to create. */
  name: string;
  /** Optional extra context, e.g. 'in categories: Catering, Decor'. */
  detail?: string;
  /** Creating a new entity (default) or renaming an existing one to this name. */
  action?: 'create' | 'rename';
}

/**
 * Warning shown before creating an entity whose name already exists.
 * Call it only when a duplicate was found; resolves true when the user
 * chooses to create another entity with the same name anyway.
 */
export function useDuplicateNameGuard() {
  const { confirm } = useDialog();

  return useCallback(
    ({ entityLabel, name, detail, action = 'create' }: DuplicateNameGuardArgs): Promise<boolean> =>
      confirm({
        title: 'Duplicate Name',
        message:
          `${withArticle(entityLabel)} named "${name}" already exists${detail ? ` ${detail}` : ''}. ` +
          (action === 'rename'
            ? `Rename this ${entityLabel} to the same name anyway?`
            : `Create another ${entityLabel} with the same name?`),
        variant: 'warning',
        confirmLabel: action === 'rename' ? 'Rename Anyway' : 'Create Anyway',
      }),
    [confirm],
  );
}

/** "a category", "an event type" — the noun with its indefinite article, capitalised. */
export function withArticle(noun: string): string {
  return `${/^[aeiou]/i.test(noun) ? 'An' : 'A'} ${noun}`;
}

/**
 * A name as duplicate detection reads it: case ignored, surrounding spaces
 * dropped and any run of spaces inside read as one — so "Bar  Mitzvah" and
 * " bar mitzvah" are the same name.
 */
export function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

/** Case- and whitespace-insensitive name equality used for duplicate detection. */
export function isSameName(a: string, b: string): boolean {
  return normalizeName(a) === normalizeName(b);
}

/**
 * The entries already called `name`, leaving out the one being edited
 * (`exceptId`). A blank name matches nothing.
 */
export function findSameNamed<T extends { id: string; name: string }>(
  items: readonly T[],
  name: string,
  exceptId?: string | null,
): T[] {
  const wanted = normalizeName(name);
  if (!wanted) return [];
  return items.filter((i) => i.id !== exceptId && normalizeName(i.name) === wanted);
}

/**
 * Asks before saving a name another entry already has. Resolves true when the
 * name is new, or when the user chose to keep the duplicate.
 */
export function useUniqueNameCheck<T extends { id: string; name: string }>(
  items: readonly T[],
  entityLabel: string,
) {
  const confirmDuplicate = useDuplicateNameGuard();
  return useCallback(
    async (name: string, exceptId?: string | null): Promise<boolean> => {
      if (findSameNamed(items, name, exceptId).length === 0) return true;
      return confirmDuplicate({
        entityLabel,
        name: name.trim(),
        action: exceptId ? 'rename' : 'create',
      });
    },
    [items, entityLabel, confirmDuplicate],
  );
}

/**
 * Whether an entry matches a search box: every word typed appears in one of
 * its fields, case ignored. An empty search matches everything.
 */
export function matchesSearch(
  query: string,
  fields: readonly (string | null | undefined)[],
): boolean {
  const words = normalizeName(query).split(' ').filter(Boolean);
  if (words.length === 0) return true;
  const haystack = fields
    .filter(Boolean)
    .map((f) => normalizeName(f as string))
    .join(' ');
  return words.every((w) => haystack.includes(w));
}
